import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  ReceiptLongOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import {
  CARD_SX,
  SECTION_ICON_BOX_SX,
} from './accountOverview.utils';

const renderInvoiceCell = (label, content) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography
      variant="caption"
      sx={{
        display: { xs: 'block', md: 'none' },
        color: 'rgba(15, 23, 42, 0.55)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      }}
    >
      {label}
    </Typography>
    {content}
  </Box>
);

export default function AccountOverviewPaymentsPanel({ invoiceRows }) {
  return (
    <Box sx={{ ...CARD_SX, p: 0 }}>
      <Box
        sx={{
          px: { xs: 2.75, md: 3 },
          py: 2.25,
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={SECTION_ICON_BOX_SX}>
            <ReceiptLongOutlined sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em', lineHeight: 1.2 }}
            >
              Pagamentos
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Histórico das últimas cobranças.
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={
            invoiceRows.length === 1
              ? '1 registro'
              : `${invoiceRows.length} registros`
          }
          sx={{
            background: 'rgba(15, 23, 42, 0.06)',
            color: '#475569',
            border: '1px solid rgba(15, 23, 42, 0.10)',
            fontWeight: 600,
            borderRadius: '8px',
          }}
        />
      </Box>
      <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
        {invoiceRows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                ...SECTION_ICON_BOX_SX,
                width: 56,
                height: 56,
                borderRadius: '14px',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ReceiptLongOutlined sx={{ fontSize: 26 }} />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5, letterSpacing: '-0.01em' }}
            >
              Nenhum pagamento por enquanto
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#64748b', maxWidth: 380, mx: 'auto' }}
            >
              Quando uma cobrança for processada, ela aparecerá aqui com data, valor e período de referência.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: '1.1fr 1fr 1.2fr 1fr 1.4fr',
                gap: 2,
                pb: 1.5,
                color: '#64748b',
                fontSize: '0.72rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
              }}
            >
              <span>Data</span>
              <span>Valor</span>
              <span>Pagamento</span>
              <span>Status</span>
              <span>Período</span>
            </Box>
            <Divider
              sx={{
                mb: 1,
                display: { xs: 'none', md: 'block' },
                borderColor: 'rgba(15, 23, 42, 0.06)',
              }}
            />
            {invoiceRows.map((invoice) => (
              <Box
                key={invoice.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: '1.1fr 1fr 1.2fr 1fr 1.4fr',
                  },
                  gap: 2,
                  py: 1.75,
                  borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                  transition: 'background 0.15s ease',
                  borderRadius: '8px',
                  mx: -1,
                  px: 1,
                  '&:hover': {
                    background: 'rgba(8, 145, 178, 0.04)',
                  },
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                {renderInvoiceCell(
                  'Data',
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#0f172a' }}
                  >
                    {invoice.date}
                  </Typography>,
                )}
                {renderInvoiceCell(
                  'Valor',
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: '#0f172a' }}
                  >
                    {invoice.amount}
                  </Typography>,
                )}
                {renderInvoiceCell(
                  'Pagamento',
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#475569' }}
                  >
                    {invoice.paymentMethod}
                  </Typography>,
                )}
                {renderInvoiceCell(
                  'Status',
                  <Chip
                    size="small"
                    label={invoice.status.label}
                    sx={{
                      bgcolor: invoice.status.bg,
                      color: invoice.status.color,
                      fontWeight: 600,
                      width: 'fit-content',
                      borderRadius: '8px',
                    }}
                  />,
                )}
                {renderInvoiceCell(
                  'Período',
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      {invoice.period}
                    </Typography>
                    {invoice.hostedInvoiceUrl && (
                      <Button
                        component="a"
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          minWidth: 0,
                          px: 0.5,
                          py: 0,
                          color: '#0891b2',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                        }}
                      >
                        Fatura
                      </Button>
                    )}
                  </Stack>,
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
